import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Clock, Globe, RotateCcw, AlertCircle, CheckCircle, Package, Calendar } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23ShippingAndExchange = () => {
    const navigate = useNavigate();

    const shippingOptions = [
        {
            icon: <Truck size={28} />,
            title: 'Standard Delivery',
            duration: '7-10 Business Days',
            price: 'FREE on orders above \u20B95,000',
            details: 'Available across all major cities and towns in India. Tracking available via SMS and email.',
            color: '#1B3A2D',
        },
        {
            icon: <Clock size={28} />,
            title: 'Express Delivery',
            duration: '3-5 Business Days',
            price: '\u20B9499',
            details: 'Guaranteed delivery for your wedding or event dates. Priority handling and dedicated tracking support.',
            color: '#C5974B',
            highlight: true,
        },
        {
            icon: <Globe size={28} />,
            title: 'International Shipping',
            duration: '10-15 Business Days',
            price: 'Calculated at checkout',
            details: 'We ship to USA, UK, UAE, Canada, Australia, and more. Customs duties may apply at destination.',
            color: '#7A1B3B',
        },
    ];

    const exchangeSteps = [
        {
            step: 1,
            title: 'Initiate Request',
            description: 'Contact us within 7 days of delivery via WhatsApp, email, or your account dashboard with your order ID and reason.',
        },
        {
            step: 2,
            title: 'Approval & Pickup',
            description: 'Our team will review your request within 24 hours. Once approved, we will arrange a free pickup from your location.',
        },
        {
            step: 3,
            title: 'Quality Check',
            description: 'The returned item is inspected to ensure it is unused, unwashed, and in original condition with all tags intact.',
        },
        {
            step: 4,
            title: 'Exchange / Refund',
            description: 'For exchanges, the new item ships within 2-3 days. For refunds, the amount is credited within 7-10 business days.',
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-shipping-cards {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 24px;
                }
                @media (min-width: 768px) {
                    .utsav-shipping-cards {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, #1B3A2D 0%, #2D5A45 100%)',
                    textAlign: 'center',
                    padding: '130px 24px 60px',
                }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h1 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(28px, 4vw, 42px)',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            margin: '0 0 12px 0',
                        }}>
                            Shipping & Exchange
                        </h1>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '16px',
                            color: 'rgba(255,255,255,0.8)',
                            margin: 0,
                            lineHeight: 1.6,
                        }}>
                            We ensure your festive wear arrives safely and on time for your special moments
                        </p>
                    </div>
                </div>

                {/* Shipping Options */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '60px 24px',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <h2 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(24px, 3vw, 32px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 8px 0',
                        }}>
                            Delivery Options
                        </h2>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            margin: 0,
                        }}>
                            Choose the shipping option that works best for your celebration timeline
                        </p>
                    </div>

                    <div className="utsav-shipping-cards">
                        {shippingOptions.map((option, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: option.highlight ? '#1B3A2D' : '#FFFFFF',
                                    borderRadius: '16px',
                                    padding: '32px',
                                    boxShadow: option.highlight
                                        ? '0 12px 40px rgba(27, 58, 45, 0.2)'
                                        : '0 2px 12px rgba(27, 58, 45, 0.06)',
                                    border: option.highlight ? 'none' : '1px solid #E8E0D4',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {option.highlight && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '16px',
                                        right: '16px',
                                        backgroundColor: '#C5974B',
                                        color: '#FFFFFF',
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                    }}>
                                        Recommended
                                    </div>
                                )}
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '14px',
                                    backgroundColor: option.highlight ? 'rgba(197, 151, 75, 0.2)' : '#F8F3EC',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: option.highlight ? '#C5974B' : option.color,
                                    marginBottom: '20px',
                                }}>
                                    {option.icon}
                                </div>
                                <h3 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    color: option.highlight ? '#FFFFFF' : '#1B3A2D',
                                    margin: '0 0 8px 0',
                                }}>
                                    {option.title}
                                </h3>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                }}>
                                    <Calendar size={14} color={option.highlight ? '#C5974B' : '#C5974B'} />
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        color: option.highlight ? '#C5974B' : '#C5974B',
                                    }}>
                                        {option.duration}
                                    </span>
                                </div>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: option.highlight ? '#FFFFFF' : '#1B3A2D',
                                    margin: '0 0 12px 0',
                                }}>
                                    {option.price}
                                </p>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: option.highlight ? 'rgba(255,255,255,0.7)' : '#5A5A5A',
                                    lineHeight: 1.6,
                                    margin: 0,
                                }}>
                                    {option.details}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Wedding Date Guarantee */}
                <div style={{
                    backgroundColor: '#F8F3EC',
                    padding: '60px 24px',
                }}>
                    <div style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}>
                        <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            backgroundColor: '#C5974B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                        }}>
                            <Calendar size={28} color="#FFFFFF" />
                        </div>
                        <h2 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(22px, 3vw, 30px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 12px 0',
                        }}>
                            Wedding Date Guarantee
                        </h2>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            lineHeight: 1.7,
                            margin: '0 0 24px 0',
                            maxWidth: '600px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}>
                            We understand that your wedding outfit cannot be late. Share your wedding date during checkout,
                            and we will guarantee delivery at least 3 days before your event. If we fail to deliver on time,
                            you receive a full refund plus express shipping compensation.
                        </p>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#FFFFFF',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(27, 58, 45, 0.06)',
                        }}>
                            <CheckCircle size={16} color="#1B3A2D" />
                            <span style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#1B3A2D',
                            }}>
                                Add your event date during checkout for priority handling
                            </span>
                        </div>
                    </div>
                </div>

                {/* Exchange Process */}
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '60px 24px',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h2 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(24px, 3vw, 32px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 8px 0',
                        }}>
                            Exchange & Return Process
                        </h2>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            margin: 0,
                        }}>
                            Simple, hassle-free returns within 7 days of delivery
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {exchangeSteps.map((item, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    gap: '20px',
                                    alignItems: 'flex-start',
                                    position: 'relative',
                                }}
                            >
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: '#1B3A2D',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <span style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        color: '#C5974B',
                                    }}>
                                        {item.step}
                                    </span>
                                </div>
                                <div style={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    padding: '20px 24px',
                                    flex: 1,
                                    boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                                    border: '1px solid #E8E0D4',
                                }}>
                                    <h3 style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: '17px',
                                        fontWeight: 700,
                                        color: '#1B3A2D',
                                        margin: '0 0 6px 0',
                                    }}>
                                        {item.title}
                                    </h3>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                        lineHeight: 1.6,
                                        margin: 0,
                                    }}>
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Important Notes */}
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '0 24px 80px',
                }}>
                    <div style={{
                        backgroundColor: '#FFF9F0',
                        borderRadius: '14px',
                        padding: '28px',
                        border: '1px solid rgba(197, 151, 75, 0.3)',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '16px',
                        }}>
                            <AlertCircle size={20} color="#C5974B" />
                            <h3 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: 0,
                            }}>
                                Important Notes
                            </h3>
                        </div>
                        <ul style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '14px',
                            color: '#5A5A5A',
                            lineHeight: 1.8,
                            margin: 0,
                            paddingLeft: '20px',
                        }}>
                            <li>Custom-made and bridal wear pieces are <strong style={{ color: '#1B3A2D' }}>non-returnable</strong> unless there is a manufacturing defect.</li>
                            <li>Items must be in <strong style={{ color: '#1B3A2D' }}>original condition</strong> — unworn, unwashed, with all tags attached and original packaging intact.</li>
                            <li>Altered or hemmed garments cannot be returned or exchanged.</li>
                            <li>For defective items, we offer full replacement or refund at no additional cost.</li>
                            <li>International returns: customer bears return shipping costs. We recommend using a tracked service.</li>
                            <li>Sale items are eligible for exchange only (no refunds) within 5 days of delivery.</li>
                        </ul>
                    </div>

                    {/* CTA */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: '40px',
                    }}>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            margin: '0 0 16px 0',
                        }}>
                            Need help with a return or have shipping questions?
                        </p>
                        <button
                            onClick={() => navigate('/template23/contact')}
                            style={{
                                padding: '14px 36px',
                                backgroundColor: '#1B3A2D',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '10px',
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '14px',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                            }}
                        >
                            Contact Support
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23ShippingAndExchange;
